import { ReactNode, createElement } from 'react';

import { PropsBridgeProvider } from '@spotify/confidence-core-react';

import type { PageContentProps } from './Content';
import { type PageHeaderProps, usePageHeaderActions } from './Header';
import { PageImpressionWrapper, usePageRootChildren } from './Wrapper';
import { PageRootBridgeProps, PageRootProps } from './types';
import {
  getPageDescriptor,
  useBreadcrumbAncestors,
  useOptionalPageBridgeProps,
} from './utils';

export type RootProps = PageRootProps & {
  title?: ReactNode;
  titleAdornment?: ReactNode;
  children: ReactNode;
};

export const Root = ({
  routeRef,
  title,
  titleAdornment,
  children,
}: RootProps) => {
  const parentBridgeProps = useOptionalPageBridgeProps();

  const descriptor = getPageDescriptor(routeRef);
  const ancestors = useBreadcrumbAncestors(routeRef);
  const { contentElement, headerElement, wrapperElements } =
    usePageRootChildren(children);

  const contentProps = contentElement?.props as PageContentProps | undefined;
  const headerProps = headerElement?.props as PageHeaderProps | undefined;
  const narrow = contentProps?.narrow;
  const pageHeaderActions = usePageHeaderActions(headerProps?.children);
  const pageHeader = headerElement ? pageHeaderActions : undefined;

  const wrapped = wrapperElements.reduceRight<ReactNode>(
    (acc, { element, component: typedComponent }) => {
      if (typedComponent) {
        return createElement(typedComponent, element.props, acc);
      }
      const { component, ...rest } = element.props;
      return createElement(component, rest, acc);
    },
    contentElement,
  );

  return (
    <PageImpressionWrapper descriptor={descriptor}>
      <PropsBridgeProvider<PageRootBridgeProps>
        {...(parentBridgeProps ?? {})}
        descriptor={descriptor}
        ancestors={ancestors}
        leafLabel={title !== undefined ? title : descriptor.title}
        titleAdornment={titleAdornment}
        pageHeader={pageHeader}
        wrapperElements={wrapperElements}
        narrow={narrow}
      >
        {wrapped}
      </PropsBridgeProvider>
    </PageImpressionWrapper>
  );
};
